<template>
	<section v-if="total > 0" class="user-evaluate-m">
		<ul class="user-evaluate-m__list" role="list">
			<li
				v-for="(item, index) in data"
				:key="item.id"
				:ref="el => bindReviewImpression(el, item, index)"
				class="review-card"
				role="listitem"
			>
				<img
					class="review-card__avatar"
					:src="
						buildOssImageUrl(item.avatar || defaultAvatar, {
							strategy: 'avatar',
							w: 96,
							h: 96,
							q: 80,
						})
					"
					:alt="item.nickname || t(`${I18N_NS}.anonymousUser`)"
				/>
				<div class="review-card__body">
					<div class="review-card__head">
						<div class="review-card__meta">
							<div class="review-card__name">
								{{ item.nickname || t(`${I18N_NS}.anonymousUser`) }}
							</div>
							<time class="review-card__time">{{ item.create_at }}</time>
						</div>
						<div class="review-card__score">
							<img
								v-for="index in STAR_COUNT"
								:key="index"
								:src="index <= item.score ? evaluateIcon : noEvaluateIcon"
								alt=""
								aria-hidden="true"
							/>
							<span>{{ item.score }}</span>
						</div>
					</div>
					<p class="review-card__content">{{ item.content }}</p>
				</div>
			</li>
		</ul>

		<div v-if="loading" class="user-evaluate-m__loading">
			<van-loading color="#333333" />
		</div>

		<button v-else-if="!isFinish" type="button" class="user-evaluate-m__more" @click="loadMore">
			{{ isEnglish ? "Load more" : "載入更多" }}
		</button>
	</section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { getEvaluateList } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { buildOssImageUrl } from "~/utils/oss-image";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultAvatar from "~/assets/images/default_avatar.png";
import evaluateIcon from "~/assets/images/pc/evaluate.png";
import noEvaluateIcon from "~/assets/images/pc/no-evaluate.png";

const I18N_NS = "teacherDetail.pc";
const STAR_COUNT = 5;
const { t, locale } = useI18n();
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	teacherId: {
		type: String,
		default: "",
	},
});
const emit = defineEmits(["has-content-change"]);

const page = ref(1);
const size = ref(6);
const total = ref(0);
const data = ref([]);
const loading = ref(false);
const isEnglish = computed(() => locale.value === "en");
const isFinish = computed(() => data.value.length >= total.value);

function bindReviewImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	impressionTracker.bind(el, `review_${item.id}`, "mentor_review_impression", {
		review_id: String(item.id),
		rating: item.score,
		position: index + 1,
	});
}

const getList = async () => {
	if (loading.value || !props.teacherId) return;
	loading.value = true;

	try {
		const currentSize = page.value > 1 ? 20 : size.value;
		const res = await getEvaluateList({
			uid: props.teacherId,
			page: page.value,
			size: currentSize,
		});

		const list = Array.isArray(res?.data?.list) ? res.data.list : [];
		const nextList = list.map(item => ({
			...item,
			score: Math.min(Math.max(Math.ceil(Number(item.score) || 0), 0), STAR_COUNT),
		}));

		if (page.value > 1) {
			data.value.push(...nextList);
		} else {
			data.value = nextList;
		}

		total.value = Number(res?.data?.pager?.total_count) || 0;
		emit("has-content-change", total.value > 0);
	} catch {
		if (page.value > 1) {
			page.value -= 1;
		} else {
			total.value = 0;
			data.value = [];
			emit("has-content-change", false);
		}
	} finally {
		loading.value = false;
	}
};

const loadMore = () => {
	if (loading.value || isFinish.value) return;
	tracking?.track?.("mentor_review_pagination_click", {
		page_index: page.value + 1,
		direction: "next",
	});
	page.value += 1;
	getList();
};

watch(
	() => props.teacherId,
	() => {
		page.value = 1;
		total.value = 0;
		data.value = [];
		getList();
	},
	{ immediate: true }
);

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.user-evaluate-m {
	width: 100%;
	padding: 0 32px;
	font-family: "Source Han Serif CN", "PingFang SC", sans-serif;

	&__list {
		display: flex;
		flex-direction: column;
		gap: 24px;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	&__loading {
		display: flex;
		justify-content: center;
		padding: 32px 0 0;
	}

	&__more {
		display: block;
		width: 220px;
		height: 56px;
		margin: 32px auto 0;
		border: 1px solid #eeeeee;
		border-radius: 8px;
		background: #ffffff;
		color: #333333;
		font-size: 24px;
	}
}

.review-card {
	display: flex;
	min-width: 0;
	padding: 24px;
	border: 1px solid #eeeeee;
	border-radius: 12px;
	background: #ffffff;

	&__avatar {
		flex: 0 0 auto;
		width: 58px;
		height: 58px;
		margin-right: 18px;
		border-radius: 50%;
		object-fit: cover;
		background: #fff2bf;
	}

	&__body {
		flex: 1;
		min-width: 0;
	}

	&__head {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 16px;
		min-width: 0;
	}

	&__meta {
		min-width: 0;
	}

	&__name {
		max-width: 220px;
		color: #333333;
		font-size: 26px;
		font-weight: 600;
		line-height: 1.25;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__time {
		display: block;
		margin-top: 8px;
		color: #999999;
		font-family: "PingFang SC", sans-serif;
		font-size: 22px;
		line-height: 1.25;
	}

	&__score {
		display: inline-flex;
		align-items: center;
		flex: 0 0 auto;
		color: #e09109;
		font-size: 22px;
		white-space: nowrap;

		img {
			width: 18px;
			height: 18px;
			margin-right: 2px;
		}

		span {
			margin-left: 4px;
		}
	}

	&__content {
		margin: 16px 0 0;
		color: #585858;
		font-family: "PingFang SC", sans-serif;
		font-size: 24px;
		line-height: 36px;
		@include line-clamp(3);
	}
}
</style>
